import { useCallback, useMemo, useState } from "react";
import { t } from "ttag";

import { useListCollectionsTreeQuery } from "metabase/api";
import { isSyncedCollection } from "metabase/collections/utils";
import { useToast } from "metabase/common/hooks";
import { buildCollectionTree } from "metabase/entities/collections";
import { Button, Icon, Loader, Tooltip } from "metabase/ui";
import { useImportChangesMutation } from "metabase-enterprise/api";

import { trackBranchSwitched, trackPullChanges } from "../../analytics";
import { useGitSyncVisible } from "../../hooks/use-git-sync-visible";
import { useRemoteSyncDirtyState } from "../../hooks/use-remote-sync-dirty-state";
import { useSyncStatus } from "../../hooks/use-sync-status";
import { type SyncError, parseSyncError } from "../../utils";
import { PushChangesModal } from "../PushChangesModal";
import {
  SyncConflictModal,
  type SyncConflictVariant,
} from "../SyncConflictModal";

import { BranchPicker } from "./BranchPicker";

interface GitSyncControlsProps {
  fullWidth?: boolean;
}

export const GitSyncControls = ({
  fullWidth = false,
}: GitSyncControlsProps) => {
  const { isVisible, currentBranch } = useGitSyncVisible();

  const [importChanges, { isLoading: isImporting }] =
    useImportChangesMutation();
  const [syncConflictVariant, setSyncConflictVariant] =
    useState<SyncConflictVariant>();
  const { isRunning: isSyncTaskRunning } = useSyncStatus();

  const [nextBranch, setNextBranch] = useState<string | null>(null);
  const [showPushModal, setShowPushModal] = useState(false);
  const [sendToast] = useToast();

  const { isDirty, refetch: refetchDirty } = useRemoteSyncDirtyState();

  // Fetch collections to filter synced ones for the push modal
  const { data: collections = [] } = useListCollectionsTreeQuery({
    "exclude-other-user-collections": true,
    "exclude-archived": true,
  });

  const syncedCollections = useMemo(() => {
    const synced = collections.filter(isSyncedCollection);
    return buildCollectionTree(synced);
  }, [collections]);

  const isSwitchingBranch = !!nextBranch;
  const isLoading = isSyncTaskRunning || isSwitchingBranch || isImporting;

  const changeBranch = useCallback(
    async (branch: string | null, isNewBranch?: boolean) => {
      if (branch == null) {
        console.warn("Trying to switch to null branch");
        return;
      }

      if (!isNewBranch) {
        await importChanges({ branch });

        trackBranchSwitched({
          triggeredFrom: "app-bar",
        });
      }

      setNextBranch(null);
    },
    [importChanges],
  );

  const handleBranchSelect = useCallback(
    async (branch: string, isNewBranch?: boolean) => {
      try {
        if (branch === currentBranch) {
          return;
        }

        setNextBranch(branch);

        const freshDirtyData = await refetchDirty().unwrap();
        const hasDirtyChanges = freshDirtyData.dirty.length > 0;

        if (hasDirtyChanges && !isNewBranch) {
          setSyncConflictVariant("switch-branch");
        } else {
          await changeBranch(branch, isNewBranch);
          setNextBranch(null);
        }
      } catch {
        sendToast({
          icon: "warning",
          toastColor: "error",
          message: t`Sorry, we were unable to switch branches.`,
        });
        setNextBranch(null);
      }
    },
    [currentBranch, changeBranch, refetchDirty, sendToast],
  );

  const handlePullClick = useCallback(async () => {
    if (!currentBranch) {
      return;
    }

    try {
      await importChanges({ branch: currentBranch }).unwrap();

      trackPullChanges({
        triggeredFrom: "app-bar",
        force: false,
      });

      sendToast({
        message: t`Your branch is now up to date with remote`,
        icon: "check",
      });
    } catch (error) {
      const { hasConflict, errorMessage } = parseSyncError(error as SyncError);

      if (hasConflict) {
        setSyncConflictVariant("pull");
        return;
      }

      sendToast({
        message: errorMessage || t`Failed to pull from remote`,
        icon: "warning",
      });
    }
  }, [currentBranch, importChanges, sendToast]);

  const handlePushClick = useCallback(() => {
    setShowPushModal(true);
  }, []);

  const handleClosePushModal = useCallback(() => {
    setShowPushModal(false);
  }, []);

  const handleCloseSyncConflictModal = useCallback(() => {
    setSyncConflictVariant(undefined);
    setNextBranch(null);
  }, []);

  if (!isVisible || !currentBranch) {
    return null;
  }

  return (
    <>
      <Button.Group
        data-testid="git-sync-controls"
        mr={fullWidth ? undefined : "2rem"}
        w={fullWidth ? "100%" : "13.5rem"}
      >
        <BranchPicker
          isLoading={isLoading}
          value={currentBranch}
          onChange={handleBranchSelect}
          baseBranch={currentBranch}
        />

        <Tooltip label={t`Pull from Git`}>
          <Button
            variant="default"
            size="compact-sm"
            px="0.5rem"
            py="1rem"
            onClick={handlePullClick}
            disabled={isLoading}
            aria-label={t`Pull from Git`}
            data-testid="git-pull-button"
            leftSection={
              isImporting ? (
                <Loader size="xs" />
              ) : (
                <Icon name="arrow_down" size={16} />
              )
            }
          />
        </Tooltip>

        <Tooltip label={isDirty ? t`Push to Git` : t`No changes to push`}>
          <Button
            variant="default"
            size="compact-sm"
            px="0.5rem"
            py="1rem"
            onClick={handlePushClick}
            disabled={isLoading || !isDirty}
            aria-label={t`Push to Git`}
            data-testid="git-push-button"
            leftSection={<Icon name="arrow_up" size={16} />}
          />
        </Tooltip>
      </Button.Group>

      {showPushModal && (
        <PushChangesModal
          onClose={handleClosePushModal}
          collections={syncedCollections}
          currentBranch={currentBranch}
        />
      )}

      {syncConflictVariant && (
        <SyncConflictModal
          collections={syncedCollections}
          currentBranch={currentBranch}
          nextBranch={nextBranch}
          onClose={handleCloseSyncConflictModal}
          variant={syncConflictVariant}
        />
      )}
    </>
  );
};
